/**
 * User-agent detection behind `forceSpecifyFormat`, private to `@yoop/datetime`.
 *
 * Every predicate reads `navigator` when called, and answers `false` when there is no `navigator`.
 */

/** Browsers that carry a `Safari/` token without being Safari. */
const NON_SAFARI_TOKENS = ['Chrom', 'CriOS', 'Edg', 'OPR', 'OPiOS', 'FxiOS', 'Android'];

/** `<Name>/<version>` tokens that carry an engine or platform version, not the browser's own. */
const NON_BROWSER_TOKENS = ['Mozilla', 'AppleWebKit', 'KHTML', 'Gecko', 'Safari', 'Mobile'];

const IOS_DEVICE = /iPhone|iPad|iPod/;
const SAFARI_VERSION = /\bVersion\/([\d._]+)/;
const PRODUCT_TOKEN = /^([A-Za-z]+)\/([\d._]+)/;
const IOS_VERSION = /\bOS (\d+)_/;
const MAC_OS_VERSION = /\bMac OS X (\d+)[._]/;

const currentNavigator = (): Navigator | undefined =>
  typeof navigator === 'undefined' ? undefined : navigator;

/** Leading integer of a version string, or `undefined` when it has none. */
const getMajorVersion = (version: string | undefined): number | undefined => {
  const majorVersion = parseInt(version ?? '', 10);
  return Number.isNaN(majorVersion) ? undefined : majorVersion;
};

/** Major version of the browser: Safari's `Version/` token, else the last product token. */
const browserMajorVersion = (): number | undefined => {
  const userAgent = currentNavigator()?.userAgent;
  if (userAgent === undefined) {
    return undefined;
  }

  // Only Safari's `Version/` names the browser. Android WebView carries a legacy `Version/4.0`
  // alongside the real `Chrome/<major>`.
  if (isSafari()) {
    const safariVersion = SAFARI_VERSION.exec(userAgent);
    if (safariVersion) {
      return getMajorVersion(safariVersion[1]);
    }
  }

  const productVersions = userAgent
    .split(/\s+/)
    .map((token) => PRODUCT_TOKEN.exec(token))
    .filter((token) => token !== null && !NON_BROWSER_TOKENS.includes(token[1]));
  return getMajorVersion(productVersions[productVersions.length - 1]?.[2]);
};

/** Major version of iOS or macOS, or `undefined` on any other platform. */
const osMajorVersion = (): number | undefined => {
  const userAgent = currentNavigator()?.userAgent;
  if (userAgent === undefined) {
    return undefined;
  }
  const version = IOS_VERSION.exec(userAgent) ?? MAC_OS_VERSION.exec(userAgent);
  return getMajorVersion(version?.[1]);
};

/** Whether the browser is Safari, desktop or mobile. */
export const isSafari = (): boolean => {
  const userAgent = currentNavigator()?.userAgent;
  return (
    userAgent !== undefined &&
    userAgent.includes('Safari/') &&
    !NON_SAFARI_TOKENS.some((token) => userAgent.includes(token))
  );
};

/** Whether the device runs iOS, including iPadOS 13+ reporting itself as a Mac. */
export const isIOS = (): boolean => {
  const client = currentNavigator();
  if (client === undefined) {
    return false;
  }
  // iPadOS 13+ reports itself as a Mac; the touch points are what tell it apart from a desktop.
  return (
    IOS_DEVICE.test(client.userAgent) ||
    (client.platform === 'MacIntel' && client.maxTouchPoints > 1)
  );
};

/** Whether the browser major version is below `majorVersion`. `false` when it is unknown. */
export const browserVersionOlderThan = (majorVersion: number): boolean => {
  const browserVersion = browserMajorVersion();
  return browserVersion !== undefined && browserVersion < majorVersion;
};

/** Whether the OS major version is below `majorVersion`. `false` when it is unknown. */
export const osVersionOlderThan = (majorVersion: number): boolean => {
  const osVersion = osMajorVersion();
  return osVersion !== undefined && osVersion < majorVersion;
};

/** Safari 13 and older, and any browser on iOS 13 and older, can't parse correctly if format isn't specified */
export const forceSpecifyFormat = (): boolean =>
  (isSafari() && browserVersionOlderThan(14)) || (isIOS() && osVersionOlderThan(14));
